/**
 * SummaryWarning — inline amber indicator used everywhere the deal-summary
 * service returns {error, missing_field, fix_hint} instead of {value}.
 *
 * The user-stated rule: never show $0 or "—" for missing data. Every field
 * either renders a real value or this warning component with an "Edit deal"
 * link that opens the deal edit modal.
 */
import React from 'react';
import { AlertTriangle } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';

export default function SummaryWarning({ error, fixHint, onEditDeal, label, missingField }) {
  const tooltipBody = (
    <div className="max-w-xs space-y-1">
      <div className="text-[11px] font-semibold text-amber-100">
        {label || 'Missing input'}
      </div>
      <div className="text-[11px] leading-snug text-amber-50">{error}</div>
      {fixHint && (
        <div className="text-[10px] text-amber-200/90 italic">{fixHint}</div>
      )}
    </div>
  );

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (onEditDeal) onEditDeal(missingField);
            }}
            className="inline-flex items-center gap-1 text-amber-700 hover:text-amber-900 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 rounded px-1"
            data-testid="summary-warning"
          >
            <AlertTriangle className="h-3.5 w-3.5 fill-amber-200/60" />
            <span className="text-xs font-medium underline decoration-dotted underline-offset-2">
              Edit deal
            </span>
          </button>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          className="bg-amber-900 border-amber-800 text-amber-50"
        >
          {tooltipBody}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
